import { useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import { FloatButton } from "antd";
import { VerticalAlignBottomOutlined } from "@ant-design/icons";
import { useUser } from "../../contexts/UserContext";
import { getFirstLetter } from "../../utils/UserInfoFormatting";
import ChatItem from "./ChatItem";
import { stripContextPrefix } from "../AskVivo/askVivo.utils";
import type { ChatMessage, SelectedSources } from "./chat";
import type { SizeType } from "antd/es/config-provider/SizeContext";

interface ChatResultsProps {
  chatHistory: Array<ChatMessage | undefined>;
  handleViewSearchSources: (
    chat: ChatMessage | undefined,
    index: number,
  ) => void;
  handleRegenerate: (
    sessionId?: string,
    input?: string,
    selectedSources?: SelectedSources,
  ) => void;
  viewSourcesOpenId: number | null;
  handleOpenCreateInsights: (chat?: ChatMessage) => void;
  isAHistoryItem: boolean;
  buttonSize?: SizeType;
  isLoading?: boolean;
}

export default function ChatResults({
  chatHistory,
  handleViewSearchSources,
  handleRegenerate,
  viewSourcesOpenId,
  handleOpenCreateInsights,
  isAHistoryItem,
  buttonSize,
}: ChatResultsProps) {
  const { userInfo } = useUser();
  const userFirstInitial = getFirstLetter(userInfo?.first_name ?? "");

  const containerRef = useRef<HTMLDivElement | null>(null);
  const bottomOfPageRef = useRef<HTMLDivElement | null>(null);
  const chatItemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [copiedInteractionId, setCopiedInteractionId] = useState<string | null>(
    null,
  );

  const displayChatHistory = useMemo(
    () =>
      chatHistory.map((chat) => ({
        ...chat,
        input: stripContextPrefix(chat?.input ?? ""),
      })),
    [chatHistory],
  );

  useEffect(() => {
    setCopiedInteractionId(null);
  }, [isAHistoryItem]);

  useLayoutEffect(() => {
    // Scroll to the top of the newest (last) ChatItem when chatHistory updates
    const lastIndex = chatHistory.length - 1;
    if (lastIndex >= 0 && chatItemRefs.current[lastIndex]) {
      chatItemRefs.current[lastIndex].scrollIntoView({
        behavior: "instant",
        block: "start",
      });
    }
    setShowScrollToBottom(false);
  }, [chatHistory]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollable = container.scrollHeight > container.clientHeight;
      const pastThreshold = container.scrollTop > 400;
      const isAtBottom =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 10;
      setShowScrollToBottom(scrollable && pastThreshold && !isAtBottom);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToBottom = () => {
    bottomOfPageRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  };

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={containerRef}
        className="bg-white hide-scrollbar"
        style={{
          height: "calc(100vh - 200px)",
          overflow: "auto",
        }}
      >
        <div className="pt-5" />
        {displayChatHistory.map((chat, index) => (
          <ChatItem
            key={chat.interaction_id || index}
            ref={(el) => (chatItemRefs.current[index] = el)}
            index={index}
            userFirstInitial={userFirstInitial}
            chat={chat}
            handleViewSearchSources={handleViewSearchSources}
            chatHistory={chatHistory}
            handleRegenerate={handleRegenerate}
            viewSourcesOpen={viewSourcesOpenId === index}
            data-testid={`chat-item-${index}`}
            handleOpenCreateInsights={handleOpenCreateInsights}
            copiedInteractionId={copiedInteractionId}
            setCopiedInteractionId={setCopiedInteractionId}
            buttonSize={buttonSize}
          />
        ))}
        <div ref={bottomOfPageRef} />
      </div>

      {showScrollToBottom && (
        <FloatButton
          icon={<VerticalAlignBottomOutlined />}
          onClick={scrollToBottom}
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: "20px",
            zIndex: 1000,
          }}
        />
      )}
    </div>
  );
}
